import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { ErrorBoundary } from './ErrorBoundary'
import { AppShell } from './AppShell'
import { UpdatePrompt } from './UpdatePrompt'
import { CameraProvider } from '@/features/scanner/CameraProvider'
import { ConfigProvider } from '@/features/config/ConfigProvider'
import { AdminSessionProvider } from '@/features/admin/AdminSessionProvider'
import { HomeScreen } from '@/screens/HomeScreen'
import { ScanScreen } from '@/screens/ScanScreen'
import { HistoryScreen } from '@/screens/HistoryScreen'
import { NotFoundScreen } from '@/screens/NotFoundScreen'

/**
 * Admin is loaded on demand — a desktop-oriented screen carrying the whole
 * workflow editor, which no scanning phone should download on first launch.
 */
const AdminScreen = lazy(() =>
  import('@/screens/AdminScreen').then((m) => ({ default: m.AdminScreen })),
)

/**
 * `BrowserRouter`, not `HashRouter`, and this is not a style preference.
 *
 * WebKit bug 215884: in a standalone (Home Screen) web app, changing the URL
 * hash revokes camera permission and forces a fresh getUserMedia prompt. Hash
 * routing in a scanning PWA would reintroduce the exact problem this rebuild
 * exists to fix.
 *
 * `CameraProvider` sits above `Routes` on purpose: owning the MediaStream here
 * rather than inside the scanner screen is what reduces the camera permission
 * prompt from once per scan to once per app launch.
 *
 * There is no login gate around the scanner. That matches how the previous app
 * worked and was a deliberate decision — these webhooks are write-only triggers
 * into Trello, Airtable and Sheets, and a login on a workshop phone costs more
 * than it protects. Admin is a different matter and is gated by a passcode the
 * Worker verifies.
 */
export function App() {
  return (
    <ErrorBoundary scope="app">
      <AdminSessionProvider>
        <ConfigProvider>
          <CameraProvider>
            <BrowserRouter>
              <Routes>
                <Route element={<AppShell />}>
                  <Route index element={<HomeScreen />} />
                  <Route path="scan" element={<ScanScreen />} />
                  <Route path="history" element={<HistoryScreen />} />
                  <Route
                    path="admin"
                    element={
                      <Suspense
                        fallback={
                          <p className="p-6 text-[0.9375rem] text-[var(--text-secondary)]">
                            Loading admin…
                          </p>
                        }
                      >
                        <AdminScreen />
                      </Suspense>
                    }
                  />
                  <Route path="*" element={<NotFoundScreen />} />
                </Route>
              </Routes>
            </BrowserRouter>
            <UpdatePrompt />
          </CameraProvider>
        </ConfigProvider>
      </AdminSessionProvider>
    </ErrorBoundary>
  )
}
